/**
 * Editor de texto rico do conteúdo dos textos padronizados (#412).
 *
 * HTML entra e HTML sai: é o formato que a emissão de PDF e de Word já espera,
 * então o campo continua sendo uma string de HTML para quem o consome. O valor
 * é controlado pelo formulário — o editor só avisa o que mudou.
 *
 * Tabela sem redimensionamento de coluna (D5): largura em documento impresso é
 * decisão do template, e o resize gravaria pixel no conteúdo.
 */
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Box, FormHelperText, Typography } from '@mui/material';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TableKit } from '@tiptap/extension-table';
import TextAlign from '@tiptap/extension-text-align';

import { htmlDeTextoLegado } from '../../utils/htmlDeTextoLegado';
import { BarraDoEditor } from './BarraDoEditor';

export interface EditorDeTextoRicoHandle {
  /** Insere texto onde o cursor está — é por aqui que a variável entra (D10). */
  inserirNoCursor: (texto: string) => void;
  focar: () => void;
}

export interface EditorDeTextoRicoProps {
  label: string;
  value: string;
  onChange: (html: string) => void;
  textoGuia?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
}

/**
 * Tabela do editor com a mesma leitura que ela terá impressa: borda fina,
 * cabeçalho com fundo. Espelha `.doc-tabela` dos templates de documento.
 */
const ESTILO_DA_AREA = {
  position: 'relative',
  p: 1.5,
  minHeight: 240,
  cursor: 'text',
  '& .ProseMirror': { outline: 'none', minHeight: 220 },
  '& .ProseMirror > *:first-of-type': { mt: 0 },
  '& table': { borderCollapse: 'collapse', width: '100%', my: 1, tableLayout: 'fixed' },
  '& th, & td': { border: 1, borderColor: 'divider', p: 0.5, verticalAlign: 'top' },
  '& th': { bgcolor: 'action.hover', fontWeight: 'bold', textAlign: 'left' },
  '& .selectedCell': { bgcolor: 'action.selected' },
} as const;

export const EditorDeTextoRico = forwardRef<EditorDeTextoRicoHandle, EditorDeTextoRicoProps>(
  ({ label, value, onChange, textoGuia, required = false, disabled = false, error = false, helperText }, ref) => {
    // O editor é criado uma vez só; o aviso de mudança precisa continuar
    // chamando o `onChange` da renderização mais recente.
    const aoMudar = useRef(onChange);
    // O que o próprio editor emitiu não volta como conteúdo novo: recarregar o
    // documento a cada tecla jogaria o cursor para o começo.
    const ultimoEmitido = useRef(value);

    useEffect(() => {
      aoMudar.current = onChange;
    }, [onChange]);

    const editor = useEditor({
      extensions: [
        StarterKit,
        TableKit.configure({ table: { resizable: false } }),
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
      ],
      content: htmlDeTextoLegado(value),
      editable: !disabled,
      editorProps: {
        attributes: {
          'aria-label': label,
          'aria-multiline': 'true',
          role: 'textbox',
        },
      },
      onUpdate: ({ editor: atual }) => {
        const html = atual.isEmpty ? '' : atual.getHTML();
        ultimoEmitido.current = html;
        aoMudar.current(html);
      },
    });

    const vazio = useEditorState({
      editor,
      selector: ({ editor: atual }) => atual?.isEmpty ?? true,
    });

    useEffect(() => {
      if (!editor || value === ultimoEmitido.current) return;
      ultimoEmitido.current = value;
      editor.commands.setContent(htmlDeTextoLegado(value), { emitUpdate: false });
    }, [editor, value]);

    useEffect(() => {
      editor?.setEditable(!disabled);
    }, [editor, disabled]);

    useImperativeHandle(
      ref,
      () => ({
        inserirNoCursor: (texto: string) => {
          editor?.chain().focus().insertContent(texto).run();
        },
        focar: () => {
          editor?.chain().focus().run();
        },
      }),
      [editor]
    );

    return (
      <Box>
        <Typography
          component="span"
          variant="body2"
          color={error ? 'error' : 'text.secondary'}
          sx={{ display: 'block', mb: 0.5 }}
        >
          {required ? `${label} *` : label}
        </Typography>

        <Box
          sx={{
            border: 1,
            borderColor: error ? 'error.main' : 'divider',
            borderRadius: 1,
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <BarraDoEditor editor={editor} disabled={disabled} />

          <Box sx={ESTILO_DA_AREA} onClick={() => editor?.chain().focus().run()}>
            {vazio !== false && textoGuia && (
              <Typography
                variant="body2"
                color="text.disabled"
                sx={{ position: 'absolute', top: 12, left: 12, pointerEvents: 'none' }}
              >
                {textoGuia}
              </Typography>
            )}
            <EditorContent editor={editor} />
          </Box>
        </Box>

        {helperText && (
          <FormHelperText error={error} sx={{ mx: 1.75 }}>
            {helperText}
          </FormHelperText>
        )}
      </Box>
    );
  }
);

EditorDeTextoRico.displayName = 'EditorDeTextoRico';

export default EditorDeTextoRico;
